import { DROPS_FILTER_SECTION_MAP, DROPS_STALE_TIME_MS, DROPS_TAB_SECTION_MAP, } from 'root/src/client/v2/_pages/drops/fixtures/drops.constants';
import { DropSerializer } from 'root/src/shared/drops/drops.constants';
import { useDropEventListFiltersContext, } from 'root/src/client/v2/_pages/drops/contexts/dropEventListFilters.context';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useEffect, useMemo, useState } from 'react';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { DROPS_ANALYTIC_EVENTS } from 'root/src/shared/drops/drops.analytics';
import { useUserId } from 'root/src/client/v2/auth/hooks/useUserId';
import { useTour } from '@reactour/tour';
import { DROPS_ONBOARDING_STEPS } from 'root/src/client/v2/_pages/drops/fixtures/dropsOnboarding.fixture';
import { useOnboardingQueryParam } from 'root/src/client/v2/onboarding/hooks/useOnboardingQueryParam';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
export const useDropsList = () => {
    var _a;
    const { tab, filter } = useDropEventListFiltersContext();
    const isAuthenticated = useIsAuthenticated();
    const isSomeCreatorChannelVerifiedQuery = trpc.creators.isSomeCreatorChannelVerified.useQuery(undefined, {
        enabled: isAuthenticated,
    });
    const dropsQuery = trpc.drops.getDrops.useQuery({
        section: DROPS_TAB_SECTION_MAP[tab],
        serializer: DropSerializer.LIST_VIEW,
        dataFilter: DROPS_FILTER_SECTION_MAP[filter],
    }, {
        staleTime: DROPS_STALE_TIME_MS,
    });
    const [isOnboarding] = useOnboardingQueryParam();
    // Memoized so the returned array keeps its identity between renders. Without this, `filteredDrops`
    // was a fresh array on every render, which defeated both `memo` on DropsGrid and the grid's own
    // search `useMemo`: every keystroke in the drops search rebuilt and re-filtered the whole list.
    const drops = useMemo(() => { var _a, _b; return (_b = (_a = dropsQuery.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []; }, [dropsQuery.data]);
    const isVerified = (_a = isSomeCreatorChannelVerifiedQuery.data) !== null && _a !== void 0 ? _a : false;
    const isAbleToJoinSponsoredDrop = useMemo(() => drops.some((drop) => {
        var _a, _b, _c, _d;
        const maxDares = (_b = (_a = drop.dropUserInfo) === null || _a === void 0 ? void 0 : _a.maxDares) !== null && _b !== void 0 ? _b : 0;
        const countOfAcceptedDares = (_d = (_c = drop.dropUserInfo) === null || _c === void 0 ? void 0 : _c.countOfAcceptedDares) !== null && _d !== void 0 ? _d : 0;
        return isVerified && !drop.isDemo && maxDares > 0 && (maxDares - countOfAcceptedDares > 0);
    }), [drops, isVerified]);
    const filteredDrops = useMemo(() => drops
        .filter((drop) => {
        if (drop.isDemo) {
            if (!isOnboarding) {
                return false;
            }
            return !isAbleToJoinSponsoredDrop;
        }
        return true;
    }), [drops, isOnboarding, isAbleToJoinSponsoredDrop]);
    useDropsAnalytics(filteredDrops);
    useDropsOnboarding(drops);
    return {
        drops: filteredDrops,
        isLoading: dropsQuery.isLoading,
    };
};
const useDropsAnalytics = (drops) => {
    const [isCaptured, setIsCaptured] = useState(false);
    const { userId = '__UNAUTHENTICATED__' } = useUserId();
    const [isOnboarding] = useOnboardingQueryParam();
    const captureEvent = () => {
        setIsCaptured(true);
        const isQualified = drops.some((drop) => {
            var _a, _b;
            const canAcceptAnyDare = ((_b = (_a = drop.dropUserInfo) === null || _a === void 0 ? void 0 : _a.maxDares) !== null && _b !== void 0 ? _b : 0) > 0;
            return canAcceptAnyDare;
        });
        const isActive = drops.some((drop) => {
            var _a, _b;
            const hasAcceptedAnyDare = ((_b = (_a = drop.dropUserInfo) === null || _a === void 0 ? void 0 : _a.countOfAcceptedDares) !== null && _b !== void 0 ? _b : 0) > 0;
            return hasAcceptedAnyDare;
        });
        if (!isQualified) {
            if (isOnboarding)
                analyticsSendItem(DROPS_ANALYTIC_EVENTS.SIGN_UP_DROP_NOT_QUALIFIED_CREATOR, userId);
            analyticsSendItem(DROPS_ANALYTIC_EVENTS.DROP_NOT_QUALIFIED_CREATOR, userId);
            return;
        }
        if (!isActive) {
            if (isOnboarding)
                analyticsSendItem(DROPS_ANALYTIC_EVENTS.SIGN_UP_DROP_INACTIVE_QUALIFIED_CREATOR, userId);
            analyticsSendItem(DROPS_ANALYTIC_EVENTS.DROP_INACTIVE_QUALIFIED_CREATOR, userId);
            return;
        }
        if (isOnboarding)
            analyticsSendItem(DROPS_ANALYTIC_EVENTS.SIGN_UP_DROP_ACTIVE_QUALIFIED_CREATOR, userId);
        analyticsSendItem(DROPS_ANALYTIC_EVENTS.DROP_ACTIVE_QUALIFIED_CREATOR, userId);
    };
    // Keyed on the length, not the array: this fires once and then no-ops, so it does not need to
    // re-run on every identity change of the list.
    useEffect(() => {
        if (drops.length && !isCaptured) {
            captureEvent();
        }
    }, [drops.length, isCaptured]);
};
const useDropsOnboarding = (drops) => {
    const tour = useTour();
    const [isOnboarding] = useOnboardingQueryParam();
    const onboardingDrop = useMemo(() => {
        const sponsoredDrop = drops
            .find((item) => {
            var _a, _b, _c, _d;
            const maxDares = (_b = (_a = item.dropUserInfo) === null || _a === void 0 ? void 0 : _a.maxDares) !== null && _b !== void 0 ? _b : 0;
            const countOfAcceptedDares = (_d = (_c = item.dropUserInfo) === null || _c === void 0 ? void 0 : _c.countOfAcceptedDares) !== null && _d !== void 0 ? _d : 0;
            return !item.isDemo && maxDares > 0 && (maxDares - countOfAcceptedDares > 0);
        });
        if (!sponsoredDrop) {
            return drops.find(d => d.isDemo);
        }
        return sponsoredDrop;
    }, [drops]);
    useEffect(() => {
        var _a;
        if (isOnboarding && onboardingDrop) {
            (_a = tour.setSteps) === null || _a === void 0 ? void 0 : _a.call(tour, DROPS_ONBOARDING_STEPS([onboardingDrop]));
            tour.setIsOpen(true);
        }
    }, [isOnboarding, onboardingDrop]);
};
